/**
 * Meta description length rules (SEO audit 2026-10-24, wave D2). Pure and unit-tested.
 *
 * Search engines cut a description at about 155-160 characters; Ahrefs flags under 100 and over 160. Every non-protected
 * description is therefore 100..158 characters long. Too long: cut at the last word boundary that fits, never mid-word.
 */

export const META_DESCRIPTION_MAX = 158;

// Words a cut sentence must not end on ("... and where to enter it at"): they are dropped as long as enough text remains.
const DANGLING_WORDS = new Set(["a", "an", "the", "to", "at", "and", "or", "of", "for", "in", "on", "with", "your", "it", "is", "are", "that", "this", "as", "by", "from", "under", "about", "into", "over", "than", "which", "what", "when", "how", "&"]);
const KEEP_AT_LEAST = 100;

/** Single spaces, no leading or trailing space (a blog meta that started with a space, writer text with line breaks). */
export function normalizeMetaText(text: string): string {
    return text.replace(/\s+/g, " ").trim();
}

/** The text cut at a word boundary to at most `max` characters; unchanged when it already fits. */
export function truncateAtWord(text: string, max: number = META_DESCRIPTION_MAX): string {
    const clean = normalizeMetaText(text);
    if (clean.length <= max) return clean;

    // `head` holds the character after the cut: when it is a space the cut is clean, otherwise back up to the last space.
    const head = clean.slice(0, max + 1);
    const lastSpace = head.lastIndexOf(" ");
    let cut = lastSpace > 0 ? head.slice(0, lastSpace) : clean.slice(0, max); // no space at all: one very long word, hard cut
    const trimEnd = (value: string) => value.replace(/[\s,;:\-–—(\[]+$/, "");
    cut = trimEnd(cut);
    for (;;) {
        const last = cut.match(/(\S+)$/)?.[1]?.toLowerCase();
        if (!last || !DANGLING_WORDS.has(last)) break;
        const shorter = trimEnd(cut.slice(0, cut.length - last.length));
        if (shorter.length < KEEP_AT_LEAST) break;
        cut = shorter;
    }
    return cut;
}

/** Longest description Ahrefs and the SERP accept without a warning. */
export const META_DESCRIPTION_LIMIT = 160;

/**
 * Safety net at render for text a writer or the admin typed (blog, CMS pages): whitespace normalised (a description that
 * started with a space), and cut at a word boundary only when it is over 160 characters. Shorter text is never padded here.
 */
export function safeMetaDescription(text: string): string {
    const clean = normalizeMetaText(text);
    return clean.length > META_DESCRIPTION_LIMIT ? truncateAtWord(clean, META_DESCRIPTION_MAX) : clean;
}
